import { ChangeEvent, FC, useContext, useEffect, useMemo, useState } from "react";
import ResourceTaggingService from "@app/resource-tagging/resourceTaggingService";
import { ManageResourceTagDisplayModel, Perspectives } from "@app/tags/types";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { OptionsListOption } from "@app/shared/qes-standards/forms/OptionsList";
import { ResourceStatus } from "@app/resources";
import { toast } from "react-toastify";
import ProjectService from "@app/projects/projectService";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";

const ResourceTagPanel: FC = () => {
  const flyoutContext = useContext(FlyoutContext);
  const [tagName, setTagName] = useState("");
  const [groupName, setGroupName] = useState("");
  const [projectName, setProjectName] = useState<string>("");
  const [projectId, setProjectId] = useState<number>(0);
  const [tags, setTags] = useState<ManageResourceTagDisplayModel[]>([]);
  const [projectOptions, setProjectOptions] = useState<OptionsListOption[]>([]);

  const resourceTaggingService = new ResourceTaggingService();
  const projectService = new ProjectService();

  const getOptions = async () => {
    try {
      const tags = await resourceTaggingService.getClientProjectTagInfo(Perspectives.All);
      setTags(tags);
      const projects = await projectService.getProjectResources(ResourceStatus.Active);
      setProjectOptions(projects.map(project => ({ key: project.resourceId + "", value: project.name })));
    } catch {
      toast("Problem loading groups and projects.",
        {
          containerId: "z-index-9999",
          type: "warning"
        }
      );
    }
  }

  useEffect(() => {
    getOptions();
  }, []);

  const onProjectOptionClick = (option: OptionsListOption) => {
    setProjectId(parseInt(option.key, 10));
  }

  const onProjectInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setProjectName(e.target.value);
    setProjectId(0);
  }

  const tagOptions = useMemo(() => {
    const distinctTags = [...new Set(tags.map(tag => tag.tagName))];
    return distinctTags.map(tag => ({ key: tag, value: tag }));
  }, [tags]);

  const groupOptions = useMemo(() => {
    const distinctGroups = [...new Set(tags.map(tag => tag.tagGroupName))];
    return distinctGroups.map(group => ({ key: group, value: group }));
  }, [tags]);

  const isDuplicate = useMemo(() => {
    const project = projectOptions.find(p => parseInt(p.key, 10) === projectId);
    const tag = tags.find(t => t.tagName === tagName && t.tagGroupName === groupName && t.projectName === project?.value);
    return !!tag;
  }, [projectId, tagName, groupName, tags, projectOptions]);

  const handleSubmit = async () => {
    try {
      const project = projectOptions.find(p => parseInt(p.key, 10) === projectId);
      if (!project) {
        toast("Unable to find project.",
          {
            containerId: "z-index-9999",
            type: "warning"
          }
        );
        return;
      }
      const tag = {
        customProjectId: projectId,
        projectName: project.value,
        tagGroupName: groupName,
        tagName: tagName
      }
      await projectService.createProjectTagsAsync([tag])
      toast("Tag created.",
        {
          containerId: "z-index-9999",
          type: "success"
        }
      );
      flyoutContext.handleCloseFlyout();
    } catch {
      toast("Something went wrong.",
        {
          containerId: "z-index-9999",
          type: "warning"
        }
      );
    }
  }

  return (
    <>
      <div className="row mt-3">
        <div className="col-8">
          <QESTextInput label="Project"
            testId="resource-tag-panel__project"
            value={projectName}
            required={true}
            isValid={!!projectId}
            placeholder="Select a project"
            optionsListConfig={{
              noMatchText: "No projects found.",
              onOptionClick: onProjectOptionClick,
              options: projectOptions,
            }}
            onChange={onProjectInputChange}
          >
          </QESTextInput>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-8">
          <QESTextInput label={"Group Name"}
            testId="resource-tag-panel__group-name"
            value={groupName}
            required={true}
            isValid={!!groupName}
            placeholder="Select or create a group"
            optionsListConfig={{
              noMatchText: "No groups found.",
              options: groupOptions,
            }}
            onChange={e => setGroupName(e.target.value)}
          >
          </QESTextInput>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-8">
          <QESTextInput label="Tag Name"
            testId="resource-tag-panel__tag-name"
            value={tagName}
            isValid={!!tagName}
            required={true}
            placeholder="Select or create a tag"
            optionsListConfig={{
              noMatchText: "No tags found.",
              options: tagOptions,
            }}
            onChange={(e) => setTagName(e.target.value)} />
        </div>
      </div>
      <FlyoutFooter submitButton={<QESButton qesTestId="resource-tag-panel__submit" onClick={handleSubmit}
                                             disabled={!tagName || !groupName || !projectId || isDuplicate}>Save</QESButton>} />
    </>
  );
}

export default ResourceTagPanel;